import { Button } from "antd";
import { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import classNames from "classnames";
import { DiscountItemType } from "src/Modules/Discounts/types";
import { RootState } from "src/store";
import ResponsiveSheet from "src/components/ResponsiveSheet/ResponsiveSheet";
import DiscountsRedeemContent from "./layouts/DiscountsRedeemContent";
import { useOrderItemMutation } from "src/store/apis/giftcards";
import { track } from "@amplitude/analytics-browser";

const REDEEM_MODAL_WIDTH = 520;

type Props = {
  data: DiscountItemType | undefined;
  imageUrl: string | undefined;
  itemCode?:string;
};

const DiscountsRedeem: FC<Props> = ({ data, imageUrl, itemCode }) => {
  const { t } = useTranslation();
  const [request, { isLoading, error, data: orderData, reset }] = useOrderItemMutation();
  const pointsBalance = useSelector((state: RootState) => state.client.userDetails.totalPointsBalance);
  const insufficientPoints = (pointsBalance ?? 0) < (data?.points ?? 0) || 0 ? true:false;
  const isResultStep = !!(error || orderData);
  const handleConfirm = () => {
    if (!data) return;
    track("Checkout Submitted", { ...data, itemCode });
    request({
      orderItems: [
        {
          itemCode,
          value: data.totalPrice,
          quantity:1,
        },
      ],
    });
  };

  useEffect(() => {
    if (!error && !orderData) return;
    track("Checkout Result Viewed", { success: !error, error, orderData, itemCode });
  }, [error, orderData]);

  const handleCheckoutInitiated = () => track("Checkout Initiated", { ...data, itemCode });

  return (
    <ResponsiveSheet
      showTitle={!isResultStep}
      label={
        <div className="flex flex-col">
          <span className="font-semibold">{t("Discounts.confirmRedemption")}</span>
          <span className="text-font-subtle text-sm font-normal">{t("Discounts.reviewOrder")}</span>
        </div>
      }
      modalProps={{ width: REDEEM_MODAL_WIDTH, afterClose: reset }}
      drawerProps={{ afterOpenChange: (isOpen) => !isOpen && reset() }}
      renderTrigger={({ open }) => (
        <Button
          size="large"
          block
          disabled={insufficientPoints}
          className={classNames("font-semibold! p-6!", insufficientPoints ? "bg-background-disabled! text-font-disabled!" : "bg-background-primary! text-font-inverted!")}
          onClick={() => {
            handleCheckoutInitiated();
            open();
          }}
        >
          {t("Discounts.redeemNow")}
        </Button>
      )}
      renderSheet={({ close }) => (
        <DiscountsRedeemContent
          data={data}
          imageUrl={imageUrl}
          onConfirm={handleConfirm}
          onClose={close}
          loading={isLoading}
          error={error}
          orderData={orderData}
          onRetry={reset}
        />
      )}
    />
  );
};

export default DiscountsRedeem;
